import '../styles/globals.css';
import '@zillow/constellation-fonts/zillow-fonts.css';
import { useRouter } from 'next/router';
import type { AppProps } from 'next/app';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { Head } from '@zg-rentals/next/app';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { useSiftScience } from '@zg-rentals/sift-science';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { setClient } from '@zg-rentals/ts-rent-credit-report-client';
import { EsusuProvider } from '../contexts/EsusuContext';
import { RentReportingAuthProvider } from '../contexts/RentReportingAuthProvider';
import Layout from '../components/Layout';
import { trackComscoreEvent } from '../analytics';

const client = getClient().extend({
  prefixUrl: process.env.NEXT_PUBLIC_PREFIX_URL,
});

setClient(client);

const MyApp = ({ Component, pageProps }: AppProps) => {
  useBootstrap(() => import('../bootstrap/browser'));
  const router = useRouter();
  const { user } = useRentalManagerUser();

  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  // todo: many apps use this event, you might need it
  // useZrmPageViewEvents(trackZrmUserEvent, router.pathname, user);

  useRegisterNotification({ ref: 'RENT_REPORTING_WEB' });

  return (
    <>
      <div data-panda-theme="zillow">
        <Head applicationName="rent-reporting-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
          <title>CreditClimb</title>
          <link rel="preconnect" href="//www.zillowstatic.com/" crossOrigin="" />
          <link
            rel="preload"
            href="//www.zillowstatic.com/s3/constellation-website/public/shared/fonts/inter/LATEST/inter-variable.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="//www.zillowstatic.com/s3/constellation-website/public/shared/fonts/open-sans/LATEST/open-sans-variable.woff2"
            as="font"
            type="font/woff2"
            crossOrigin=""
          />
          {/* Visual refresh fonts
            https://constellation.pages.zgtools.net/constellation/main/?path=/docs/documentation-font-loading-getting-started--api#if-you-need-visual-refresh-theme-fonts 
            */}
        </Head>
        <RpErrorBoundary>
          <DefaultQueryProvider
            defaultOptions={{
              queries: {
                refetchOnWindowFocus: false,
                staleTime: 3 * 60 * 1000,
              },
            }}
          >
            <RentReportingAuthProvider>
              <EsusuProvider>
                <Layout>
                  <div style={{ maxWidth: '1280px', margin: 'auto', padding: '16px' }}>
                    <Component {...pageProps} />
                  </div>
                </Layout>
              </EsusuProvider>
            </RentReportingAuthProvider>
          </DefaultQueryProvider>
        </RpErrorBoundary>
      </div>
    </>
  );
};

export default MyApp;
